import React from 'react';
import { Link } from 'react-router-dom';
import {
  ClipboardDocumentCheckIcon,
  UserCircleIcon,
  ChatBubbleLeftRightIcon,
  DocumentCheckIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';
import { usePageMeta } from '../../hooks/usePageMeta';
import Navigation from '../../components/Navigation';
import Breadcrumb from '../../components/Navigation/Breadcrumb';
import InternalLinks from '../../components/Navigation/InternalLinks';

export default function DentalConsentGuide() {
  usePageMeta(
    'Dental consent guide for UK practices | SmileConsent',
    'A practical guide to informed consent in UK dentistry: what to cover, how to document it, and how digital tools fit with GDC and CQC expectations.',
    'https://smileconsent.co.uk/dental-consent-guide'
  );

  const steps = [
    {
      title: 'Establish dialogue',
      body: 'Consent is ongoing. Explain the proposed treatment in plain language, invite questions, and check understanding — not only at the first visit.',
      icon: ChatBubbleLeftRightIcon,
    },
    {
      title: 'Cover material information',
      body: 'Include benefits, material risks, reasonable alternatives (including no treatment), and costs. What counts as “material” depends on the patient and the procedure.',
      icon: ClipboardDocumentCheckIcon,
    },
    {
      title: 'Record capacity and support',
      body: 'Where relevant, note how capacity was assessed and any support given. Follow the Mental Capacity Act 2005 (England & Wales) or your jurisdiction’s rules.',
      icon: UserCircleIcon,
    },
    {
      title: 'Document and retain',
      body: 'Keep dated records of what was discussed and agreed. Signed forms support evidence but do not replace good notes of the conversation.',
      icon: DocumentCheckIcon,
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        <Breadcrumb
          items={[
            { name: 'Home', href: '/' },
            { name: 'Consent guide', href: '/dental-consent-guide' },
          ]}
        />
      </div>

      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 pt-4">
        <header className="mb-10">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight">
            Dental consent guide (UK practices)
          </h1>
          <p className="mt-4 text-lg text-gray-600 leading-relaxed">
            A concise checklist-style guide for teams who want consent that stands up to professional,
            regulatory, and indemnity scrutiny. This is general information, not legal advice.
          </p>
        </header>

        <ol className="space-y-8">
          {steps.map((s, i) => (
            <li
              key={s.title}
              className="rounded-2xl border border-gray-200 bg-slate-50/60 p-6 sm:p-8"
            >
              <div className="flex gap-4">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue-600 text-white font-bold">
                  {i + 1}
                </span>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <s.icon className="h-6 w-6 text-blue-600 shrink-0" aria-hidden />
                    <h2 className="text-lg font-bold text-gray-900">{s.title}</h2>
                  </div>
                  <p className="mt-3 text-gray-700 leading-relaxed">{s.body}</p>
                </div>
              </div>
            </li>
          ))}
        </ol>

        <section className="mt-12 rounded-2xl border border-gray-200 p-6 sm:p-8">
          <h2 className="text-xl font-bold text-gray-900">Digital consent</h2>
          <p className="mt-3 text-gray-700 leading-relaxed">
            Sending structured, UK-aware information for the patient to read and sign can strengthen the
            record — provided the clinical conversation still happens and the record reflects it. Choose tools
            that fit your governance and indemnifier.
          </p>
        </section>

        <div className="mt-12 flex flex-col sm:flex-row gap-4">
          <Link
            to="/dental-consent-law-uk"
            className="inline-flex items-center justify-center gap-2 rounded-xl border-2 border-gray-300 px-6 py-3 font-semibold text-gray-800 hover:border-blue-500 hover:text-blue-700 transition-colors"
          >
            Consent law in the UK
            <ArrowRightIcon className="h-4 w-4" />
          </Link>
          <Link
            to="/cpd-legal-consent"
            className="inline-flex items-center justify-center gap-2 rounded-xl border-2 border-gray-300 px-6 py-3 font-semibold text-gray-800 hover:border-blue-500 hover:text-blue-700 transition-colors"
          >
            Free CPD: legal consent
            <ArrowRightIcon className="h-4 w-4" />
          </Link>
        </div>

        <div className="mt-12">
          <InternalLinks
            title="Treatment-Specific Consent Examples"
            links={[
              {
                title: 'Implant consent form UK',
                description: 'Example treatment page for implant risks, alternatives, and aftercare',
                path: '/implant-consent-form-uk',
              },
              {
                title: 'Orthodontic consent form UK',
                description: 'Consent records for braces, aligners, retention, and relapse',
                path: '/orthodontic-consent-form-uk',
              },
              {
                title: 'Wisdom tooth consent form UK',
                description: 'Surgical extraction consent with recovery and complication discussion',
                path: '/wisdom-tooth-consent-form-uk',
              },
              {
                title: 'Sedation consent form UK',
                description: 'Consent guidance for sedation suitability, escort, and recovery',
                path: '/sedation-consent-form-uk',
              },
            ]}
          />
        </div>

        <div className="mt-10 rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-700 text-white p-8 sm:p-10">
          <h2 className="text-xl font-bold">Try SmileConsent</h2>
          <p className="mt-2 text-blue-100 leading-relaxed">
            AI-generated consent text, multiple treatments, and digital signing — built for UK dental teams.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <Link
              to="/register"
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-white px-6 py-3 font-semibold text-blue-700 hover:bg-blue-50 transition-colors"
            >
              Start free trial
              <ArrowRightIcon className="h-4 w-4" />
            </Link>
            <Link
              to="/book-demo"
              className="inline-flex items-center justify-center gap-2 rounded-xl border-2 border-white/80 px-6 py-3 font-semibold text-white hover:bg-white/10 transition-colors"
            >
              Book a demo
            </Link>
          </div>
        </div>
      </article>
    </div>
  );
}
